import { MixedAccountsTableItem } from 'selectors/linkageSelectors'
import { SortDirection, SortParam } from 'components/features/pfm/LinkedAccounts/OrderBy'
import * as orderBy from 'lodash/orderBy'
import * as groupBy from 'lodash/groupBy'

type WrapperAccount =
  & MixedAccountsTableItem
  & {
    item: MixedAccountsTableItem
    inSpan: number
    groupSum: number
    groupByValue: MixedAccountsTableItem[keyof MixedAccountsTableItem] | null
  }

const PINNED_ORDER: ['pinnedRate', 'createdAt'] = ['pinnedRate', 'createdAt']
const PINNED_DIR: ['asc', 'desc'] = ['asc', 'desc']

export const invertDirection = (direction: SortDirection) => {
  return direction === 'asc' ? 'desc' : 'asc'
}

type OrderParams = [
  (keyof WrapperAccount)[],
  SortDirection[]
]
const orderFieldsMap: Record<SortParam, (keyof WrapperAccount)[]> = {
  providerName:        [...PINNED_ORDER, 'providerName', 'groupSum', 'providerGroupByKey', 'unlinkedRate'],
  name:                [...PINNED_ORDER, 'nameRate', 'name'],
  amount:              [...PINNED_ORDER, 'summableAmount', 'unlinkedRate'],
  categoryName:        [...PINNED_ORDER, 'categoryName', 'unlinkedRate', 'summableAmount'],
}
const orderByMap: Record<SortParam, (direction: SortDirection) => OrderParams> = {
  providerName:        (dir) => ([orderFieldsMap.providerName, [...PINNED_DIR, dir, invertDirection(dir), dir, dir]]),
  name:                (dir) => ([orderFieldsMap.name, [...PINNED_DIR, dir, dir]]),
  amount:              (dir) => ([orderFieldsMap.amount, [...PINNED_DIR, dir, invertDirection(dir)]]),
  categoryName:        (dir) => ([orderFieldsMap.categoryName, [...PINNED_DIR, dir, dir, invertDirection(dir)]]),
}

export const groupAccounts = (accountsTableItems: MixedAccountsTableItem[], sortBy: SortParam, sortDirection: SortDirection) => {
  const result = makeAccountsGroups(accountsTableItems, sortBy)
  const orderedResult = orderBy(result, ...orderByMap[sortBy](sortDirection))
  return orderedResult
}

const makeAccountsGroups = (accountsTableItems: MixedAccountsTableItem[], sortBy: SortParam): WrapperAccount[] => {
  if (sortBy === 'providerName') {
    return makeGroups(accountsTableItems, 'providerGroupByKey', 'summableAmount')

  } else if (sortBy === 'categoryName') {
    return makeGroups(accountsTableItems, 'categoryName', 'summableAmount')

  } else {
    return accountsTableItems.map((item) => ({
      item,
      ...item,
      inSpan: 0,
      groupSum: 0,
      groupByValue: null,
    }))
  }
}
const makeGroups = (accounts: MixedAccountsTableItem[], groupByField: keyof MixedAccountsTableItem, sumBy?: keyof MixedAccountsTableItem): WrapperAccount[] => {
  const grouped = groupBy(accounts, groupByField)
  return Object.keys(grouped).reduce((items, key) => {
    const group = grouped[key]

    let groupSum = 0
    if (sumBy) {
      groupSum = group.reduce((sum, item) => {
        const addValue = +(item[sumBy] || 0) || 0
        return sum + addValue
      }, 0)
    }

    const newItems = group.map((item) => ({
      item,
      ...item,
      inSpan: group.length - 1,
      groupSum,
      groupByValue: item[groupByField],
    }))

    return [
      ...items,
      ...newItems,
    ]
  }, [] as WrapperAccount[])
}
